/**
 * Quiet procedural spectacle bed: an endlessly descending tonal staircase,
 * counter-rotating filtered air, breath-width motion, and rare trapdoor drops.
 * It is additive and bounded; narration and music remain the primary program.
 */
export const DEFAULT_SPATIAL_TRANCE = Object.freeze({
  enabled: true,
  intensity: 0.42,
  spiralEnabled: true,
  spiralSeconds: 32,
  spiralRadius: 0.72,
  spiralDescent: 0.58,
  staircaseEnabled: true,
  staircaseLevel: 0.007,
  staircaseSeconds: 28,
  vortexEnabled: true,
  vortexLevel: 0.005,
  breathRoomDepth: 0.34,
  trapdoorEnabled: true,
  trapdoorLevel: 0.012,
  trapdoorIntervalSeconds: 210,
});

const clamp = (value, min, max, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(min, Math.min(max, number)) : fallback;
};

export function normalizeSpatialTrance(value = {}) {
  const d = DEFAULT_SPATIAL_TRANCE;
  return {
    enabled: value.enabled === undefined ? d.enabled : Boolean(value.enabled),
    intensity: clamp(value.intensity, 0, 1, d.intensity),
    spiralEnabled: value.spiralEnabled === undefined ? d.spiralEnabled : Boolean(value.spiralEnabled),
    spiralSeconds: clamp(value.spiralSeconds, 16, 90, d.spiralSeconds),
    spiralRadius: clamp(value.spiralRadius, 0.2, 1.4, d.spiralRadius),
    spiralDescent: clamp(value.spiralDescent, 0, 1.2, d.spiralDescent),
    staircaseEnabled: value.staircaseEnabled === undefined ? d.staircaseEnabled : Boolean(value.staircaseEnabled),
    staircaseLevel: clamp(value.staircaseLevel, 0, 0.018, d.staircaseLevel),
    staircaseSeconds: clamp(value.staircaseSeconds, 16, 60, d.staircaseSeconds),
    vortexEnabled: value.vortexEnabled === undefined ? d.vortexEnabled : Boolean(value.vortexEnabled),
    vortexLevel: clamp(value.vortexLevel, 0, 0.014, d.vortexLevel),
    breathRoomDepth: clamp(value.breathRoomDepth, 0, 0.75, d.breathRoomDepth),
    trapdoorEnabled: value.trapdoorEnabled === undefined ? d.trapdoorEnabled : Boolean(value.trapdoorEnabled),
    trapdoorLevel: clamp(value.trapdoorLevel, 0, 0.025, d.trapdoorLevel),
    trapdoorIntervalSeconds: clamp(value.trapdoorIntervalSeconds, 90, 600, d.trapdoorIntervalSeconds),
    breathInSeconds: clamp(value.breathInSeconds, 2.5, 5, 3.5),
    breathOutSeconds: clamp(value.breathOutSeconds, 2.5, 6, 4),
  };
}

export const SPATIAL_TRANCE_WORKLET_SOURCE = `
class SpatialTranceSpectacleProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.s = ${JSON.stringify(DEFAULT_SPATIAL_TRANCE)};
    this.time = 0; this.seed = 0x714ac3; this.air = [0, 0];
    this.tonePhase = [0, 0, 0, 0]; this.dropPhase = 0;
    this.port.onmessage = ({data}) => { if (data?.type === 'settings') this.s = {...this.s, ...data.settings}; };
  }
  random() { let x=this.seed; x^=x<<13; x^=x>>>17; x^=x<<5; this.seed=x>>>0; return this.seed/4294967296; }
  process(inputs, outputs) {
    const out=outputs[0]; if(!out?.[0]) return true;
    const L=out[0], R=out[1]||out[0], sr=sampleRate, tau=Math.PI*2, s=this.s;
    const master=s.enabled ? Math.max(0,Math.min(1,Number(s.intensity)||0)) : 0;
    const descent=Math.max(16,Number(s.staircaseSeconds)||28);
    const orbit=Math.max(16,Number(s.spiralSeconds)||32);
    const breath=Math.max(5,(Number(s.breathInSeconds)||3.5)+(Number(s.breathOutSeconds)||4));
    const interval=Math.max(90,Number(s.trapdoorIntervalSeconds)||210);
    for(let i=0;i<L.length;i++) {
      const stair=(this.time/descent)%1;
      let sl=0,srOut=0;
      if(s.staircaseEnabled) for(let k=0;k<4;k++) {
        const position=(k/4 + 1-stair)%1;
        const hz=52*Math.pow(2,position*2.4);
        this.tonePhase[k]=(this.tonePhase[k]+tau*hz/sr)%tau;
        const window=Math.pow(Math.sin(Math.PI*position),1.7);
        const pan=Math.sin(tau*(this.time/orbit + k/4))*.62;
        const sample=Math.sin(this.tonePhase[k])*window*(Number(s.staircaseLevel)||0);
        sl+=sample*Math.sqrt((1-pan)*.5); srOut+=sample*Math.sqrt((1+pan)*.5);
      }
      let vl=0,vr=0;
      if(s.vortexEnabled) {
        const alpha=1-Math.exp(-tau*720/sr);
        for(let c=0;c<2;c++){const w=this.random()*2-1;this.air[c]+=alpha*(w-this.air[c]);}
        const breathWidth=1+(Number(s.breathRoomDepth)||0)*Math.sin(tau*this.time/breath);
        const pan=Math.sin(tau*this.time/orbit)*.78*breathWidth;
        const level=Number(s.vortexLevel)||0;
        vl=this.air[0]*level*Math.sqrt(Math.max(0,(1-pan)*.5));
        vr=this.air[1]*level*Math.sqrt(Math.max(0,(1+pan)*.5));
      }
      let drop=0;
      const local=this.time%interval;
      // Establish the room before the first cinematic transition.
      if(s.trapdoorEnabled && this.time>=interval && local<3.2) {
        const attack=Math.min(1,local/.7), decay=Math.exp(-Math.max(0,local-.7)/.62);
        const env=attack*decay;
        const hz=120-82*Math.min(1,local/2.4);
        this.dropPhase=(this.dropPhase+tau*hz/sr)%tau;
        drop=Math.sin(this.dropPhase)*env*(Number(s.trapdoorLevel)||0);
      }
      L[i]=(sl+vl+drop*.707)*master; R[i]=(srOut+vr+drop*.707)*master;
      this.time+=1/sr;
    }
    return true;
  }
}
registerProcessor('spatial-trance-spectacle', SpatialTranceSpectacleProcessor);`;

const registered = new WeakMap();
async function ensureWorklet(context) {
  if (!context?.audioWorklet || typeof AudioWorkletNode === "undefined") return false;
  if (registered.has(context)) return registered.get(context);
  const promise = (async () => {
    const blob = new Blob([SPATIAL_TRANCE_WORKLET_SOURCE], { type: "text/javascript" });
    const url = URL.createObjectURL(blob);
    try { await context.audioWorklet.addModule(url); return true; } finally { URL.revokeObjectURL(url); }
  })().catch(() => false);
  registered.set(context, promise); return promise;
}

export class SpatialTranceEngine {
  constructor(context, destination = context?.destination) {
    this.context = context; this.destination = destination; this.node = null;
    this.settings = normalizeSpatialTrance();
  }
  async start(value = {}) {
    this.settings = normalizeSpatialTrance(value);
    if (!this.node) {
      if (!await ensureWorklet(this.context)) return false;
      this.node = new AudioWorkletNode(this.context, "spatial-trance-spectacle", { numberOfInputs: 0, numberOfOutputs: 1, outputChannelCount: [2] });
      this.node.connect(this.destination);
    }
    return this.apply(this.settings);
  }
  apply(value = {}) {
    this.settings = normalizeSpatialTrance({ ...this.settings, ...value });
    this.node?.port.postMessage({ type: "settings", settings: this.settings });
    return Boolean(this.node);
  }
  stop() { this.apply({ enabled: false }); }
  destroy() { try { this.node?.disconnect(); } catch {} this.node = null; }
}
